import { DestroyRef, Directive, effect, ElementRef, inject, input, model, NgZone, output, signal } from '@angular/core';
import { logger } from './logger';
import { TrgDragDropService } from './TrgDragDrop';
import { TrgDrag } from './TrgDrag.directive';
import { interval, Subscription, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const LOGGER = logger('trgDrop');

const trgDropDebug = signal(false);

(window as any).trgDropDebug = function (enabled: boolean) {
  trgDropDebug.set(enabled);
};

@Directive({
  selector: '[trgDrop]',
  host: {
    'class': 'trg-drop',
    '[class.trg-drop-dropping]': 'dropping()',
  },
  standalone: false,
  exportAs: 'TrgDropDirective'
})
export class TrgDropDirective {
  readonly ngZone = inject(NgZone);

  readonly destroyRef = inject(DestroyRef);
  readonly dragDrop = inject(TrgDragDropService);

  /**
   * One or more tags that link this drop to drags.
   * A drag must have at least one tag that is within this set to be accepted by this drop.
   * If null, this drop only allows drags that have null tags as well.
   **/
  readonly dropTags = input<string | string[]>(null);

  /** 
   * Event triggered when a drag enters this drop.
   * This only fires for a drag that is allowed to enter (e.g. filtered by tags).
   **/
  readonly dropEnter = output<TrgDrop.DropEvent>({ alias: 'trgDropEnter' });

  /** 
   * Event triggered when a drag exited this drop.
   * This fires for a drag that left this drop, for any reason (e.g. tags changed, moved out).
   **/
  readonly dropExit = output<TrgDrop.DropEvent>({ alias: 'trgDropExit' });

  /**
   * Event triggered when a drag has been dropped on this drop.
   **/
  readonly dropped = output<TrgDrop.DropEvent>({ alias: 'trgDropped' });

  /** 
   * Store unique id of the drop that you can reference 
   * from drag/drop events. It does not have functional impact otherwise. */
  readonly dropId = input<any>();

  /**
   * Assign true to disable this drop.
   **/
  readonly dropDisabled = input(false);

  /**
   * Boolean indicating whether anything is being help on top
   * of this drop, ready to be dropped in (i.e. something entered this drop, but has not exited it).
   **/
  readonly dropping = model(false);

  /**
   * Predicate to check if a drag can be dropped into this drop.
   * If unassigned, all drags are allowed (after default filtering, e.g. tags).
   **/
  readonly dropAllowed = input<TrgDrop.DropPredicate>(null);

  /**
   * Priority of this drop between other drops that a drag can enter.
   * Priority takes precedence over distance, e.g. priority 1 will always
   * be selected over priority 0, if the drag is above both drops.
   **/
  readonly dropPriority = input(0);

  /** 
   * Expands the interaction bounds of this drop by this amount of pixels.
   * Options are: all sides, [horizontal, vertical] or [left, right, top, bottom].
   **/
  readonly dropExpand = input<number | number[]>(0);

  /**
   * Applies restriction to this drop, so that its interaction bounds 
   * don't exceed a specific element's own rectangle.
   * When `true` or `xy`, the bounds are restricted both horizontally and vertically.
   * When `x` or `y`, the bounds are restricted horizontally or vertically only.
   * When false, bounds are not restricted.
   **/
  readonly dropRectRestrictToParent = input<boolean | 'x' | 'y' | 'xy'>();

  /**
   * When `dropRectRestrictToParent` is not `false`, defined the element to restrict drop bounds to. 
   * When unassigned, uses the parent element of the element the directive is applied to.
   **/
  readonly dropRestrictParent = input<HTMLElement>();

  private _element = inject<ElementRef<HTMLElement>>(ElementRef);
  get element() { return this._element.nativeElement; }

  private _destroyed = false;
  get destroyed() { return this._destroyed; }

  ngOnInit() {
    this.dragDrop.drops.add(this);
  }

  ngOnDestory() {
    this._destroyed = true;

    this.dragDrop.drops.delete(this);
  }

  onDropEnter(e: TrgDrag.DragEvent) {
    if (this.destroyed)
      return;

    this.ngZone.run(() => {
      this.dropEnter.emit({ ...e, drop: this });
      this.dropping.set(true);
    });
  }

  onDropExit(e: TrgDrag.DragEvent) {
    if (this.destroyed)
      return;

    this.ngZone.run(() => {
      this.dropExit.emit({ ...e, drop: this });
      this.dropping.set(false);
    });
  }

  onDrop(e: TrgDrag.DragEvent) {
    if (this.destroyed)
      return;

    this.ngZone.run(() => {
      this.dropping.set(false);
      this.dropped.emit({ ...e, drop: this });
    });
  }

  canDrop(e: TrgDrag.DragEvent) {
    if (this.destroyed)
      return null;

    if (this.dropDisabled())
      return null;

    let dist = this.testDropRect(e);
    if (dist === null)
      return null;

    let predicate = this.dropAllowed();

    if (predicate) {
      try {
        if (!predicate({ ...e, drop: this }))
          return null;
      }
      catch (err) {
        LOGGER.error(err);
        return null;
      }
    }

    return {
      drop: this,
      dist,
      priority: this.dropPriority()
    };
  }

  testDropRect(e: TrgDrag.DragEvent) {
    if (this.destroyed)
      return null;

    let { x, y } = e;

    let { minX, maxX, minY, maxY, centerX, centerY } = this.getDropRect();

    let checkX = e.drag.dragAxis().includes('x');
    let checkY = e.drag.dragAxis().includes('y');

    if (checkX) {
      if (x < minX || x > maxX)
        return null;
    }

    if (checkY) {
      if (y < minY || y > maxY)
        return null;
    }

    let distX = centerX - x;
    let distY = centerY - y;
    let dist = Math.sqrt(distX * distX + distY * distY);
    return { dist };
  }

  private getDropRect() {
    let rect = this.element.getBoundingClientRect();

    let expand = this.dropExpand();
    let expandList =
      typeof expand === 'number'
        ? [expand, expand]
        : expand;

    // Grab expansion offsets (minX = left, maxX = right, minY = top, maxY = bottom)
    let [expandMinX, expandMaxX, expandMinY, expandMaxY] =
      expandList.length == 2
        ? [expandList[0], expandList[0], expandList[1], expandList[1]]
        : expandList;

    let minX = rect.left   - expandMinX;
    let maxX = rect.right  + expandMaxX;
    let minY = rect.top    - expandMinY;
    let maxY = rect.bottom + expandMaxY;

    let parentRestriction = this.dropRectRestrictToParent();
    if (parentRestriction === true)
      parentRestriction = 'xy';

    if (parentRestriction) {
      let parent = this.dropRestrictParent() ?? this.element.parentElement;
      if (parent) {
        // When parent restriction is enabled, clamp min/max to the parent 
        // element's own rectangle

        let parentRect = parent.getBoundingClientRect();

        if (parentRestriction.indexOf('x') != -1) {
          let parentMinX = parentRect.left;
          let parentMaxX = parentRect.right;

          minX = Math.max(parentMinX, minX);
          maxX = Math.min(parentMaxX, maxX);
        }

        if (parentRestriction.indexOf('y') != -1) {
          let parentMinY = parentRect.top;
          let parentMaxY = parentRect.bottom;

          minY = Math.max(parentMinY, minY);
          maxY = Math.min(parentMaxY, maxY);
        }
      }
    }

    let centerX = minX + (maxX - minX) / 2;
    let centerY = minY + (maxY - minY) / 2;

    return { minX, maxX, minY, maxY, centerX, centerY };
  }


  private debugElement: HTMLElement;
  private debugElementInner: HTMLElement;
  private debugUpdateInterval = interval(100).pipe(
    takeUntilDestroyed(this.destroyRef),
    tap(() => this.updateDebugElement())
  );

  private debugUpdateIntervalSub: Subscription;

  private readonly trgDropDebugEffect = effect(() => {
    let debug = trgDropDebug();
    if (debug) {
      this.updateDebugElement();
      this.debugUpdateIntervalSub ??= this.debugUpdateInterval.subscribe();
    }
    else {
      if (this.debugElement?.parentElement)
        this.debugElement.parentElement.removeChild(this.debugElement);

      this.debugUpdateIntervalSub?.unsubscribe();
      this.debugUpdateIntervalSub = null;
    }
  });

  private updateDebugElement() {
    if (!this.debugElement) {
      let color = debugColours[nextDebugColourIndex++ % debugColours.length];

      this.debugElementInner = document.createElement('div');
      this.debugElementInner.style.setProperty('background', color);

      this.debugElement = document.createElement('div');
      this.debugElement.classList.add('trg-drop-debug-rect');
      this.debugElement.style.setProperty('background', color);

      this.debugElement.appendChild(this.debugElementInner);
    }

    if (!this.debugElement.parentElement)
      document.body.appendChild(this.debugElement);

    let rect1 = this.element.getBoundingClientRect();
    let rect2 = this.getDropRect();

    this.debugElementInner.style.setProperty('left',   `${rect1.left - rect2.minX}px`);
    this.debugElementInner.style.setProperty('top',    `${rect1.top  - rect2.minY}px`);
    this.debugElementInner.style.setProperty('width',  `${rect1.width}px`);
    this.debugElementInner.style.setProperty('height', `${rect1.height}px`);

    this.debugElement.style.setProperty('left',   `${rect2.minX}px`);
    this.debugElement.style.setProperty('top',    `${rect2.minY}px`);
    this.debugElement.style.setProperty('width',  `${Math.abs(rect2.maxX - rect2.minX)}px`);
    this.debugElement.style.setProperty('height', `${Math.abs(rect2.maxY - rect2.minY)}px`);

    if (this.dropping()) {
      this.debugElement.style.setProperty('opacity', '1');
    }
    else {
      this.debugElement.style.setProperty('opacity', '0.3');
    }
  }

  ngOnDestroy() {
    this.debugElement?.parentElement?.removeChild(this.debugElement);
  }
};

export namespace TrgDrop {
  export interface DropEvent extends TrgDrag.DragEvent {
    readonly drop: TrgDropDirective;
  }

  export type DropPredicate = (e: DropEvent) => boolean;
}

let nextDebugColourIndex = 0;

const debugColours = [
  "hsla(0.00,40%,72%,0.3)",
  "hsla(137.51,57%,85%,0.3)",
  "hsla(275.02,53%,83%,0.3)",
  "hsla(52.52,49%,81%,0.3)",
  "hsla(190.03,45%,79%,0.3)",
  "hsla(327.54,41%,77%,0.3)",
  "hsla(105.05,58%,75%,0.3)",
  "hsla(242.55,54%,73%,0.3)",
  "hsla(20.06,50%,86%,0.3)",
  "hsla(157.57,46%,84%,0.3)",
  "hsla(295.08,42%,82%,0.3)",
  "hsla(72.59,59%,80%,0.3)",
  "hsla(210.09,55%,78%,0.3)",
  "hsla(347.60,51%,76%,0.3)",
  "hsla(125.11,47%,74%,0.3)",
  "hsla(262.62,43%,73%,0.3)",
  "hsla(30.13,58%,85%,0.3)",
  "hsla(167.64,54%,83%,0.3)",
  "hsla(305.14,50%,81%,0.3)",
  "hsla(82.65,46%,79%,0.3)",
  "hsla(220.16,42%,77%,0.3)",
  "hsla(357.67,59%,75%,0.3)",
  "hsla(195.18,55%,73%,0.3)",
  "hsla(332.68,51%,72%,0.3)",
  "hsla(110.19,47%,86%,0.3)",
  "hsla(247.69,43%,84%,0.3)",
  "hsla(35.20,40%,82%,0.3)",
  "hsla(172.71,57%,80%,0.3)",
  "hsla(310.22,53%,78%,0.3)",
  "hsla(87.73,49%,76%,0.3)",
  "hsla(225.24,45%,74%,0.3)",
  "hsla(2.75,41%,73%,0.3)",
  "hsla(140.26,58%,85%,0.3)",
  "hsla(277.76,54%,83%,0.3)",
  "hsla(55.27,50%,81%,0.3)",
  "hsla(192.78,46%,79%,0.3)",
  "hsla(330.50,42%,77%,0.3)",
  "hsla(107.01,59%,75%,0.3)",
  "hsla(244.52,55%,73%,0.3)",
  "hsla(22.03,51%,72%,0.3)",
  "hsla(159.53,47%,86%,0.3)",
  "hsla(297.04,43%,84%,0.3)",
  "hsla(74.55,40%,82%,0.3)",
  "hsla(212.06,58%,80%,0.3)",
  "hsla(349.57,54%,78%,0.3)",
  "hsla(126.08,50%,76%,0.3)",
  "hsla(263.59,46%,74%,0.3)",
  "hsla(31.09,42%,73%,0.3)",
  "hsla(168.60,59%,85%,0.3)",
  "hsla(305.11,55%,83%,0.3)",
  "hsla(83.61,51%,81%,0.3)",
  "hsla(221.18,47%,79%,0.3)",
  "hsla(358.63,43%,77%,0.3)",
  "hsla(196.14,40%,75%,0.3)",
  "hsla(333.64,58%,86%,0.3)",
  "hsla(111.15,54%,84%,0.3)",
  "hsla(248.66,50%,82%,0.3)",
  "hsla(36.17,46%,80%,0.3)",
  "hsla(173.68,42%,78%,0.3)",
  "hsla(311.09,59%,76%,0.3)",
  "hsla(88.67,55%,74%,0.3)",
  "hsla(226.20,51%,73%,0.3)",
  "hsla(3.71,47%,72%,0.3)",
];
